import React from 'react';
import { Text, TextStyle } from 'react-native';
import { useRouter } from 'expo-router';
import { useAuth } from '@/contexts/AuthContext';

interface FormattedTextProps {
  content: string;
  style?: TextStyle;
  numberOfLines?: number;
}

interface TextSegment {
  text: string;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  strikethrough?: boolean;
  mention?: string;
}

const MENTION_PATTERN = /@([a-zA-Z0-9._]+)/g;

function splitMentions(
  text: string,
  baseFormats: Omit<TextSegment, 'text' | 'mention'>
): TextSegment[] {
  const result: TextSegment[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  MENTION_PATTERN.lastIndex = 0;
  while ((match = MENTION_PATTERN.exec(text)) !== null) {
    if (match.index > lastIndex) {
      result.push({ text: text.slice(lastIndex, match.index), ...baseFormats });
    }
    result.push({ text: match[0], mention: match[1], ...baseFormats });
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) {
    result.push({ text: text.slice(lastIndex), ...baseFormats });
  }
  return result.length > 0 ? result : [{ text, ...baseFormats }];
}

export default function FormattedText({ content, style, numberOfLines }: FormattedTextProps) {
  const router = useRouter();
  const { user, userProfile } = useAuth();

  const handleMentionPress = (username: string) => {
    // If the tapped mention is the current user, navigate with userId directly
    // so the profile screen never needs to do a username→id resolution
    const isSelf =
      user?.id &&
      userProfile?.username &&
      userProfile.username.toLowerCase() === username.toLowerCase();

    if (isSelf) {
      router.push({
        pathname: '/community-profile',
        params: { userId: user!.id },
      });
    } else {
      router.push({
        pathname: '/community-profile',
        params: { username },
      });
    }
  };

  const parseContent = (html: string): TextSegment[] => {
    if (!html.includes('<')) {
      return splitMentions(html, {});
    }

    const segments: TextSegment[] = [];
    let currentFormats = {
      bold: false,
      italic: false,
      underline: false,
      strikethrough: false,
    };
    const formatStack: typeof currentFormats[] = [];
    const parts = html.split(/(<\/?[^>]+>)/);

    try {
      for (const part of parts) {
        if (part.startsWith('<')) {
          const isClosing = part.startsWith('</');
          const tagName = part.replace(/<\/?([^>]+)>/, '$1').toLowerCase();

          if (isClosing) {
            if (formatStack.length > 0) {
              currentFormats = formatStack.pop() || {
                bold: false,
                italic: false,
                underline: false,
                strikethrough: false,
              };
            }
          } else {
            formatStack.push({ ...currentFormats });
            switch (tagName) {
              case 'b':
              case 'strong':
                currentFormats.bold = true;
                break;
              case 'i':
              case 'em':
                currentFormats.italic = true;
                break;
              case 'u':
                currentFormats.underline = true;
                break;
              case 's':
              case 'strike':
                currentFormats.strikethrough = true;
                break;
            }
          }
        } else if (part.length > 0) {
          segments.push(...splitMentions(part, { ...currentFormats }));
        }
      }
    } catch {
      return splitMentions(html.replace(/<[^>]*>/g, ''), {});
    }

    return segments;
  };

  const getSegmentStyle = (segment: TextSegment): TextStyle => {
    const s: TextStyle = {};

    if (segment.mention) {
      s.color = '#6B9EC2';
      s.fontFamily = 'Inter_700Bold';
      s.fontWeight = 'bold';
      return s;
    }

    if (segment.bold) {
      s.fontWeight = 'bold';
      s.fontFamily = 'Inter_700Bold';
    }
    if (segment.italic) {
      s.fontStyle = 'italic';
    }

    const decorations: string[] = [];
    if (segment.underline) decorations.push('underline');
    if (segment.strikethrough) decorations.push('line-through');
    if (decorations.length > 0) {
      s.textDecorationLine = decorations.join(' ') as TextStyle['textDecorationLine'];
    }

    return s;
  };

  const segments = parseContent(content);

  return (
    <Text style={style} numberOfLines={numberOfLines}>
      {segments.map((segment, index) =>
        segment.mention ? (
          <Text
            key={index}
            style={getSegmentStyle(segment)}
            onPress={() => handleMentionPress(segment.mention!)}
            onStartShouldSetResponder={() => true}
          >
            {segment.text}
          </Text>
        ) : (
          <Text key={index} style={getSegmentStyle(segment)}>
            {segment.text}
          </Text>
        )
      )}
    </Text>
  );
}
